import type { UserClass } from "../api-client/models";
import type { Item } from "../api-client/models/item";
import type { Trade } from "../api-client/models/trade";

export interface PermissionConfig {
  key: string;
  errorMessage: (roles: string) => string;
  additionalCheck?: (user: UserClass, ...args: any[]) => string | undefined;
  requiresSelfApproval?: {
    permission: string;
    createdByCheck: (...args: any[]) => number | null;
    errorMessage: (roles: string) => string;
  };
}

// Helper function to create simple permission configs
const simplePermission = (
  key: string,
  errorMessage: string | ((roles: string) => string),
): PermissionConfig => ({
  key,
  errorMessage: typeof errorMessage === 'string'
    ? (roles: string): string => errorMessage.replace('{roles}', roles)
    : errorMessage,
});

// Helper for ownership-based permissions
const nationOwnershipPermission = (
  key: string,
  errorMessage: string | ((roles: string) => string),
  ownershipError: string,
): PermissionConfig => ({
  key,
  errorMessage: typeof errorMessage === 'string'
    ? (roles: string): string => errorMessage.replace('{roles}', roles)
    : errorMessage,
  additionalCheck: (user: UserClass, nationId: number): string | undefined => {
    if (user.nation.id !== nationId) {
      return ownershipError;
    }
    return undefined;
  },
});

export const PERMISSIONS = {
  // Infrastructure permissions
  PURCHASE_INFRASTRUCTURE: simplePermission(
    'CAN_PURCHASE_INFRASTRUCTURE',
    (roles: string): string => `The ${roles} has permissions to purchase infrastructure`,
  ),

  APPROVE_INFRASTRUCTURE: {
    key: 'CAN_PURCHASE_INFRASTRUCTURE',
    errorMessage: (roles: string): string => `The ${roles} can approve infrastructure`,
    requiresSelfApproval: {
      permission: 'CAN_PURCHASE_INFRASTRUCTURE',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (roles: string): string =>
        `Not allowed to approve your own infrastructure purchase. The ${roles} can approve infrastructure.`,
    },
  } satisfies PermissionConfig,

  PLACE_INFRASTRUCTURE: simplePermission(
    'CAN_PLACE_INFRASTRUCTURE',
    (roles) => `The ${roles} must place infrastructure`,
  ),

  // Military permissions
  PURCHASE_MILITARY: {
    key: 'CAN_PURCHASE_MILITARY',
    errorMessage: (roles: string): string => `The ${roles} has permissions to purchase military`,
    additionalCheck: (user: UserClass): string | undefined => {
      if (!user.nation.hasMilitaryBase) {
        return 'Must build and place military base';
      }
      return undefined;
    },
  } satisfies PermissionConfig,

  APPROVE_MILITARY: {
    key: 'CAN_PURCHASE_MILITARY',
    errorMessage: (roles: string): string => `The ${roles} has permissions to approve military`,
    requiresSelfApproval: {
      permission: 'CAN_PURCHASE_MILITARY',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string =>
        'Not allowed to approve your own military purchase. The prime minister or defense minister has permissions to approve military.',
    },
  } satisfies PermissionConfig,

  PLACE_MILITARY: simplePermission(
    'CAN_PLACE_MILITARY',
    (roles) => `The ${roles} must place military`,
  ),

  // Nation management permissions
  UPLOAD_FLAG: nationOwnershipPermission(
    'CAN_UPLOAD_FLAG',
    (roles) => `The ${roles} must update the flag`,
    'You do not have permissions to update other nations flags',
  ),

  EDIT_CONSTITUTION: nationOwnershipPermission(
    'CAN_EDIT_CONSTITUTION',
    (roles) => `The ${roles} must edit the constitution`,
    'You do not have permissions to edit other nations constitutions',
  ),

  EDIT_NATION_NAME: nationOwnershipPermission(
    'CAN_EDIT_CONSTITUTION',
    (roles) => `The ${roles} must set your nations name`,
    'You do not have permissions to edit other nations names',
  ),

  EDIT_PURCHASE_NAME: simplePermission(
    'CAN_EDIT_PURCHASE_NAME',
    (roles) => `The ${roles} must edit name`,
  ),

  // Trade permissions
  INITIATE_TRADES: simplePermission(
    'CAN_INITIATE_TRADES',
    (roles) => `The ${roles} must initiate trades`,
  ),

  APPROVE_OUTGOING_TRADES: {
    key: 'CAN_INITIATE_TRADES',
    errorMessage: (roles: string): string => `The ${roles} must approve outgoing trades`,
    additionalCheck: (user: UserClass, trade: Trade): string | undefined => {
      if (trade.offeringNation.id !== user.nation.id) {
        return 'You are not allowed to approve other nations trades';
      }
      return undefined;
    },
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_TRADES',
      createdByCheck: (_user: UserClass, trade: Trade): number | null => trade.createdBy,
      errorMessage: (roles: string): string =>
        `The ${roles} must approve outgoing trades. You cannot approve your own trade.`,
    },
  } satisfies PermissionConfig,

  CANCEL_OUTGOING_TRADES: {
    key: 'CAN_INITIATE_TRADES',
    errorMessage: (roles: string): string => `The ${roles} must cancel outgoing trades`,
    additionalCheck: (user: UserClass, trade: Trade): string | undefined => {
      if (trade.offeringNation.id != user.nation.id) {
        return 'You are not allowed to cancel trades from other nations';
      }
      return undefined;
    },
  } satisfies PermissionConfig,

  ACCEPT_INCOMING_TRADES: {
    key: 'CAN_INITIATE_TRADES',
    errorMessage: (roles: string): string => `The ${roles} must accept incoming trades`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_TRADES',
      createdByCheck: (_user: UserClass, trade: Trade): number | null => trade.createdBy,
      errorMessage: (): string => 'You are not allowed to accept or reject your own trade',
    },
  } satisfies PermissionConfig,

  // Trade alliance permissions
  INITIATE_TRADE_ALLIANCE: simplePermission(
    'CAN_INITIATE_TRADE_ALLIANCE',
    (roles) => `The ${roles} are responsible for trade alliances`,
  ),

  APPROVE_OUTGOING_TRADE_ALLIANCE: {
    key: 'CAN_INITIATE_TRADE_ALLIANCE',
    errorMessage: (roles: string): string => `The ${roles} are responsible for trade alliances`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_TRADE_ALLIANCE',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string => 'Not allowed to approve your own alliance',
    },
  } satisfies PermissionConfig,

  CANCEL_OUTGOING_TRADE_ALLIANCE: simplePermission(
    'CAN_INITIATE_TRADE_ALLIANCE',
    (roles) => `The ${roles} are responsible for trade alliances`,
  ),

  ACCEPT_INCOMING_TRADE_ALLIANCE: {
    key: 'CAN_INITIATE_TRADE_ALLIANCE',
    errorMessage: (roles: string): string => `The ${roles} are responsible for trade alliances`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_TRADE_ALLIANCE',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string => 'Not allowed to approve your own alliance',
    },
  } satisfies PermissionConfig,

  CANCEL_INCOMING_TRADE_ALLIANCE: simplePermission(
    'CAN_INITIATE_TRADE_ALLIANCE',
    (roles) => `The ${roles} are responsible for trade alliances`,
  ),

  INITIATE_PEACE_TREATY: simplePermission(
    'CAN_INITIATE_WAR',
    (roles) => `The ${roles} are responsible for peace treaties`,
  ),

  APPROVE_OUTGOING_PEACE_TREATY: {
    key: 'CAN_INITIATE_WAR',
    errorMessage: (roles: string): string => `The ${roles} are responsible for peace treaties`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_WAR',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (roles: string): string =>
        `The ${roles} must approve peace treaties. You cannot approve your own treaty.`,
    },
  } satisfies PermissionConfig,

  // Military alliance permissions
  INITIATE_MILITARY_ALLIANCE: simplePermission(
    'CAN_INITIATE_MILITARY_ALLIANCE',
    (roles) => `The ${roles} are responsible for military alliances`,
  ),

  APPROVE_OUTGOING_MILITARY_ALLIANCE: {
    key: 'CAN_INITIATE_MILITARY_ALLIANCE',
    errorMessage: (roles: string): string => `The ${roles} are responsible for military alliances`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_MILITARY_ALLIANCE',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string => 'Not allowed to approve your own alliance',
    },
  } satisfies PermissionConfig,

  CANCEL_OUTGOING_MILITARY_ALLIANCE: simplePermission(
    'CAN_INITIATE_MILITARY_ALLIANCE',
    (roles) => `The ${roles} are responsible for military alliances`,
  ),

  CANCEL_MILITARY_ALLIANCE: simplePermission(
    'CAN_INITIATE_MILITARY_ALLIANCE',
    (roles) => `The ${roles} are responsible for military alliances`,
  ),

  ACCEPT_INCOMING_MILITARY_ALLIANCE: {
    key: 'CAN_INITIATE_MILITARY_ALLIANCE',
    errorMessage: (roles: string): string => `The ${roles} are responsible for military alliances`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_MILITARY_ALLIANCE',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string => 'Not allowed to accept your own alliance',
    },
  } satisfies PermissionConfig,

  CANCEL_INCOMING_MILITARY_ALLIANCE: simplePermission(
    'CAN_INITIATE_MILITARY_ALLIANCE',
    (roles) => `The ${roles} are responsible for military alliances`,
  ),

  // Warning and war permissions
  INITIATE_WARNING: simplePermission(
    'CAN_INITIATE_WARNING',
    (roles) => `The ${roles} are responsible for warnings`,
  ),

  APPROVE_OUTGOING_WARNING: {
    key: 'CAN_INITIATE_WARNING',
    errorMessage: (roles: string): string => `The ${roles} are responsible for warnings`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_WARNING',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (roles: string): string =>
        `Not allowed to approve your own warning. ${roles} can approve warnings. `,
    },
  } satisfies PermissionConfig,

  INITIATE_WAR: simplePermission(
    'CAN_INITIATE_WAR',
    (roles) => `War must be initialized by the ${roles}`,
  ),

  APPROVE_OUTGOING_WAR: {
    key: 'CAN_INITIATE_WAR',
    errorMessage: (roles: string): string => `Wars must be approved by the ${roles}`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_WAR',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string =>
        'Not allowed to approve your own war. Wars must be approved by the prime minister or secretary of state. ',
    },
  } satisfies PermissionConfig,

  // Subpoena permissions
  INITIATE_SUBPOENA: simplePermission(
    'CAN_INITIATE_SUBPOENA',
    (roles) => `The ${roles} must initiate subpoenas`,
  ),

  APPROVE_OUTGOING_SUBPOENA: {
    key: 'CAN_INITIATE_SUBPOENA',
    errorMessage: (roles: string): string => `The ${roles} must approve subpoenas`,
    requiresSelfApproval: {
      permission: 'CAN_INITIATE_SUBPOENA',
      createdByCheck: (_user: UserClass, id: number): number => id,
      errorMessage: (): string => 'Not allowed to approve your own subpoenas',
    },
  } satisfies PermissionConfig,

  // News and comment permissions
  PUBLISH_NEWS: simplePermission(
    'CAN_PUBLISH_NEWS',
    (roles) => `The ${roles} has permission to publish news`,
  ),

  EDIT_NEWS: nationOwnershipPermission(
    'CAN_PUBLISH_NEWS',
    (roles) => `The ${roles} has permission to edit articles`,
    'You do not have permissions to edit this article',
  ),

  COMMENT: simplePermission(
    'CAN_COMMENT',
    () => 'You do not have permissions to comment',
  ),

  // Profile permissions
  EDIT_PROFILE: {
    key: '',
    errorMessage: (): string => '',
    additionalCheck: (user: UserClass, id: number): string | undefined => {
      if (user && user.id != id) {
        return 'You do not have permissions to edit this profile';
      }
      return undefined;
    },
  } satisfies PermissionConfig,

  // Item purchase permissions (special logic)
  PURCHASE_ITEM: {
    key: '',
    errorMessage: (): string => '',
    additionalCheck: (user: UserClass, item: Item): string | undefined => {
      if (
        item.name === 'Informal Settlement' ||
        item.name === 'Large Informal Settlement'
      ) {
        return 'Cannot build informal settlements';
      }
      if (item.name === 'Fishing Boat') {
        if (!user.nation.hasTradePort) {
          return 'Must build trade port';
        }
      }
      if (item.name === 'City') {
        if (!user.nation.hasVillage) {
          return 'Must build village';
        }
      }
      if (
        item.name === 'Megalopolis' ||
        item.name === 'University' ||
        item.name === 'Military Base'
      ) {
        if (!user.nation.hasCity) {
          return 'Must build city';
        }
      }
      return undefined;
    },
  } satisfies PermissionConfig,

  // Note: PLACE_PURCHASE is handled specially in permissionService
  // because it needs to dynamically choose between two different permissions
} as const;
